import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { friendlyGenerationError } from "../../utils/generationErrors"

// IMPORTANT: keep this component declared at module scope. Declaring it
// inside ImageGallery's body would create a new component identity on every
// parent render, which makes React unmount/remount every <img> on each
// keystroke in the prompt textarea — causing the gallery to flicker and the
// images to re-fetch repeatedly.
const LazyImage = React.memo(({ src, alt, style, onClick }) => {
  const imgRef = useRef(null)
  const [isInView, setIsInView] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    const element = imgRef.current
    if (!element) return
    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setIsInView(true)
              observer.unobserve(entry.target)
            }
          })
        },
        { root: null, rootMargin: "200px", threshold: 0.01 }
      )
      observer.observe(element)
      return () => observer.disconnect()
    } else {
      setIsInView(true)
    }
  }, [])

  return (
    <>
      {!isLoaded && isInView && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "#fce4ec",
            background:
              "linear-gradient(90deg, #fce4ec 25%, #fdf2f7 50%, #fce4ec 75%)",
            backgroundSize: "200% 100%",
            animation: "shimmer 1.5s infinite",
            borderRadius: "8px",
          }}
        />
      )}
      <img
        ref={imgRef}
        src={isInView ? src : undefined}
        data-src={src}
        alt={alt}
        style={{
          ...style,
          opacity: isLoaded ? 1 : 0,
          transition: "opacity 0.3s ease-in-out",
        }}
        onClick={onClick}
        onLoad={() => setIsLoaded(true)}
        loading="lazy"
        decoding="async"
      />
    </>
  )
})

// GalleryItem is also hoisted to module scope for the same reason as
// LazyImage above. Everything it needs comes in via the `ctx` prop, which is
// memoized in ImageGallery so React.memo can short-circuit re-renders for
// items whose `img` and `ctx` references haven't changed.
const GalleryItem = React.memo(({ img, ctx }) => {
  const {
    isMobile,
    compactControls,
    selectedVideoSourceUrl,
    onUsePrompt,
    onUseForVideo,
    onHideImage,
    setFullscreenImage,
    handleDownload,
    downloadingId,
    styles,
  } = ctx
  const isDownloading = downloadingId === img.id
  const [isHovered, setIsHovered] = useState(false)
  const showControls = isMobile ? true : isHovered
  const isSelectedForVideo = !!(
    selectedVideoSourceUrl &&
    img.imageUrl &&
    selectedVideoSourceUrl === img.imageUrl
  )
  return (
    <div
      style={{
        ...styles.galleryImageContainerStyle,
        ...(isSelectedForVideo
          ? { outline: "3px solid #ff4fb8", outlineOffset: "-3px" }
          : {}),
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {showControls && img.prompt && onUsePrompt && !img.isLoading && !img.isFailed && (
        <button
          title="Use this prompt"
          style={styles.usePromptButtonStyle}
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            onUsePrompt(img.prompt)
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
          </svg>
          {!compactControls && (
            <span style={{ lineHeight: "12px" }}>Use prompt</span>
          )}
        </button>
      )}
      {showControls && !img.isLoading && !img.isFailed && (
        <button
          title={
            isDownloading
              ? "Preparing download…"
              : img.mediaType === "video"
                ? "Download video"
                : "Download image"
          }
          aria-busy={isDownloading || undefined}
          disabled={isDownloading}
          style={
            isDownloading
              ? styles.downloadButtonActiveStyle
              : styles.downloadButtonStyle
          }
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            if (isDownloading) return
            const url = img.mediaType === "video" ? img.videoUrl : img.imageUrl
            handleDownload(url, img.id)
          }}
        >
          {isDownloading ? (
            <span
              className="download-button-spinner"
              aria-label="Downloading"
              role="status"
            />
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
          )}
        </button>
      )}
      {showControls &&
        img.mediaType !== "video" &&
        !img.isLoading &&
        !img.isFailed &&
        img.imageUrl && (
          <button
            title="Enlarge image"
            style={styles.zoomButtonStyle}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              setFullscreenImage(img.imageUrl)
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              <line x1="11" y1="8" x2="11" y2="14"></line>
              <line x1="8" y1="11" x2="14" y2="11"></line>
            </svg>
          </button>
        )}
      {showControls &&
        onUseForVideo &&
        img.mediaType !== "video" &&
        !img.isLoading &&
        !img.isFailed &&
        img.imageUrl && (
          <button
            type="button"
            title={
              isSelectedForVideo
                ? "Selected for video"
                : "Use this image for video"
            }
            style={
              isSelectedForVideo
                ? styles.useForVideoSelectedOverlayStyle
                : styles.useForVideoOverlayStyle
            }
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              onUseForVideo(img.imageUrl, img)
            }}
            onMouseEnter={(e) => {
              if (isSelectedForVideo) return
              e.currentTarget.style.transform =
                "translateX(-50%) translateY(-2px)"
              e.currentTarget.style.boxShadow =
                "0 12px 26px rgba(255, 79, 184, 0.65)"
            }}
            onMouseLeave={(e) => {
              if (isSelectedForVideo) return
              e.currentTarget.style.transform = "translateX(-50%)"
              e.currentTarget.style.boxShadow =
                "0 8px 22px rgba(255, 79, 184, 0.55)"
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polygon points="23 7 16 12 23 17 23 7"></polygon>
              <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
            </svg>
            <span>
              {isSelectedForVideo ? "Selected for video" : "Use for video"}
            </span>
          </button>
        )}
      {showControls && (
        <button
          title="Remove from gallery"
          style={styles.deleteButtonStyle}
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            if (onHideImage) onHideImage(img)
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6l-1 14H6L5 6"></path>
            <path d="M10 11v6"></path>
            <path d="M14 11v6"></path>
            <path d="M9 6V4h6v2"></path>
          </svg>
        </button>
      )}
      {img.isLoading ? (
        <div style={styles.placeholderStyle}>
          {img.mediaType === "video" && img.imageUrl && (
            <img
              src={img.imageUrl}
              alt="generating"
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                opacity: 0.45,
                borderRadius: "8px",
              }}
            />
          )}
          <div
            style={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "12px",
              maxWidth: "90%",
            }}
          >
            <div style={styles.spinnerStyle}></div>
            {img.mediaType === "video" && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "6px",
                  padding: "10px 14px",
                  borderRadius: "14px",
                  background:
                    "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(253,242,247,0.95) 100%)",
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                  border: "1px solid rgba(217, 106, 147, 0.18)",
                  boxShadow: "0 6px 18px rgba(255, 79, 184, 0.18)",
                  animation: "videoLoadingPulse 2.4s ease-in-out infinite",
                }}
              >
                <div
                  style={{
                    fontSize: "13px",
                    color: "#7a3753",
                    fontWeight: 700,
                    letterSpacing: "0.2px",
                    lineHeight: 1.1,
                  }}
                >
                  Generating video<span className="video-loading-dots" />
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    fontSize: "11px",
                    color: "#a16280",
                    fontWeight: 500,
                    lineHeight: 1.2,
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="9" />
                    <polyline points="12 7 12 12 15 14" />
                  </svg>
                  <span>can take up to 15 min</span>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : img.isFailed ? (
        (() => {
          const friendly = friendlyGenerationError(img.error, img.mediaType)
          return (
            <div style={styles.failedStyle}>
              <div style={styles.failedInnerStyle}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ marginBottom: "4px" }}
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <div
                  style={{
                    fontSize: "18px",
                    fontWeight: "500",
                    marginTop: "8px",
                  }}
                >
                  {friendly.title}
                </div>
                <div style={styles.errorMessageStyle}>{friendly.message}</div>
              </div>
            </div>
          )
        })()
      ) : img.mediaType === "video" ? (
        <video
          src={img.videoUrl}
          poster={img.imageUrl || undefined}
          controls
          playsInline
          preload="metadata"
          style={{
            width: "100%",
            height: "auto",
            display: "block",
            borderRadius: "8px",
            backgroundColor: "#000",
          }}
        />
      ) : (
        <LazyImage
          src={img.imageUrl}
          alt={img.prompt || "Processed image"}
          style={styles.galleryImageStyle}
          onClick={() => setFullscreenImage(img.imageUrl)}
        />
      )}
    </div>
  )
})

const ImageGallery = ({
  isLoadingInitial,
  galleryImages,
  isMobile,
  stacked = false,
  setFullscreenImage,
  onHideImage,
  onUsePrompt,
  onUseForVideo,
  selectedVideoSourceUrl,
  emptyText = "Your generated images will appear here.",
  columns,
}) => {
  // Default columns: mobile = 2, desktop = 2 (legacy behaviour). Caller can override (e.g. 1 or 3).
  const effectiveColumns = columns ?? (isMobile ? 2 : 2)
  const gapPx = isMobile ? 8 : 12
  const flexBasis = `calc(${100 / effectiveColumns}% - ${(gapPx * (effectiveColumns - 1)) / effectiveColumns}px)`
  const tileMinHeight = effectiveColumns >= 3 ? 160 : effectiveColumns === 1 ? 320 : 240
  // State für Progressive Loading: Zeige erstmal nur die ersten 4 Bilder
  const [displayedCount, setDisplayedCount] = useState(4)
  const loadMoreTriggerRef = useRef(null)
  const IMAGES_PER_LOAD = 4
  // Welches Bild/Video gerade heruntergeladen wird (id), damit der Download-
  // Button sofort visuelles Feedback (Spinner) gibt – sonst denkt der User der
  // Klick sei verloren gegangen, bis das Browser-Save-Dialog ein paar Sekunden
  // später erscheint.
  const [downloadingId, setDownloadingId] = useState(null)

  // Reset displayedCount wenn sich galleryImages ändert (z.B. nach Login)
  useEffect(() => {
    setDisplayedCount(4)
  }, [galleryImages.length])

  // IntersectionObserver für Infinite Scroll
  useEffect(() => {
    const triggerElement = loadMoreTriggerRef.current
    if (!triggerElement || displayedCount >= galleryImages.length) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Wenn mehr als 50% des Trigger-Elements sichtbar ist
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            // RequestAnimationFrame für smoother Update
            requestAnimationFrame(() => {
              setDisplayedCount((prev) => 
                Math.min(prev + IMAGES_PER_LOAD, galleryImages.length)
              )
            })
          }
        })
      },
      { 
        root: null, 
        rootMargin: "100px", 
        threshold: 0.5 
      }
    )

    observer.observe(triggerElement)
    return () => observer.disconnect()
  }, [displayedCount, galleryImages.length])
  
  // Memoize die anzuzeigenden Bilder um unnötige Re-renders zu vermeiden
  const displayedImages = useMemo(
    () => galleryImages.slice(0, displayedCount),
    [galleryImages, displayedCount]
  )

  // Memoize Gallery-Container Style
  const galleryGridStyle = useMemo(() => ({
    display: "flex",
    flexWrap: "wrap",
    gap: `${gapPx}px`,
    transform: "translateZ(0)", // GPU-Layer für stabiles Rendering
    backfaceVisibility: "hidden", // Verhindert Flickering
    WebkitBackfaceVisibility: "hidden",
  }), [gapPx])

  const spinKeyframes = `
    @keyframes spin { 
      0% { transform: rotate(0deg); } 
      100% { transform: rotate(360deg); } 
    }
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
    @keyframes videoLoadingPulse {
      0%, 100% {
        box-shadow: 0 6px 18px rgba(255, 79, 184, 0.18);
        transform: translateY(0);
      }
      50% {
        box-shadow: 0 10px 24px rgba(255, 79, 184, 0.28);
        transform: translateY(-1px);
      }
    }
    .video-loading-dots {
      display: inline-block;
      width: 14px;
      text-align: left;
      margin-left: 2px;
    }
    .video-loading-dots::after {
      content: "";
      animation: videoLoadingDots 1.4s steps(4, end) infinite;
    }
    @keyframes videoLoadingDots {
      0%   { content: ""; }
      25%  { content: "."; }
      50%  { content: ".."; }
      75%  { content: "..."; }
      100% { content: ""; }
    }
    .download-button-spinner {
      display: inline-block;
      width: 12px;
      height: 12px;
      border: 2px solid rgba(255, 255, 255, 0.4);
      border-top-color: #ffffff;
      border-radius: 50%;
      animation: spin 0.7s linear infinite;
    }
  `

  const galleryAreaStyle = useMemo(() => ({
    width: "100%",
    maxWidth: "100%",
    flex: stacked ? "none" : 1,
    overflowY: isMobile || stacked ? "visible" : "auto",
    overflowX: "hidden",
    boxSizing: "border-box",
    padding: stacked ? "0" : isMobile ? "15px" : "20px",
    backgroundColor: stacked ? "transparent" : "#ffffff",
    borderRadius: stacked ? "0" : isMobile ? "8px" : "8px 0 0 0",
    boxShadow: stacked ? "none" : "0 2px 10px rgba(74, 26, 44, 0.06)",
    transform: "translateZ(0)", // GPU-Beschleunigung
    willChange: "scroll-position", // Browser Hint für Scroll-Optimierung
    backfaceVisibility: "hidden",
    WebkitBackfaceVisibility: "hidden",
    WebkitFontSmoothing: "antialiased", // Smoother rendering
  }), [isMobile, stacked])
  const galleryImageContainerStyle = useMemo(() => ({
    position: "relative",
    flex: `1 1 ${flexBasis}`,
    background: "#fce4ec",
    borderRadius: "10px",
    boxShadow: "0 2px 6px rgba(74, 26, 44, 0.08)",
    overflow: "hidden",
    minHeight: `${tileMinHeight}px`,
    maxWidth: flexBasis,
    willChange: "auto", // Browser-Optimierung für Animationen
    contain: "layout style paint", // CSS Containment für bessere Performance
    backfaceVisibility: "hidden", // Anti-Flicker
    WebkitBackfaceVisibility: "hidden",
  }), [flexBasis, tileMinHeight])
  // Icon-Größe + Abstand: auf Mobile etwas kleiner/enger, damit auf schmalen
  // Bildkacheln (2-Spalten Mobile) der "Use prompt"-Button mit Text links und
  // die rechten Icon-Buttons sich nicht überlappen.
  const iconBtnSize = isMobile ? 24 : 28
  const iconBtnGap = isMobile ? 6 : 8
  // Auf Mobile werden die rechten Icon-Buttons vertikal gestapelt (Delete oben,
  // dann Download, dann Zoom darunter), damit links Platz für "Use prompt" mit
  // Text bleibt. Auf Desktop bleibt die horizontale Reihe.
  const stackIconsVertical = isMobile
  const iconStep = iconBtnSize + iconBtnGap
  // Position-Helper: index 0 = oberster/rechtester Button (Delete)
  const iconPos = (index) =>
    stackIconsVertical
      ? {
          top: `${iconBtnGap + index * iconStep}px`,
          right: `${iconBtnGap}px`,
        }
      : {
          top: `${iconBtnGap}px`,
          right: `${iconBtnGap + index * iconStep}px`,
        }
  const deletePos = iconPos(0)
  const downloadPos = iconPos(1)
  const zoomPos = iconPos(2)

  const deleteButtonStyle = useMemo(() => ({
    position: "absolute",
    top: deletePos.top,
    right: deletePos.right,
    zIndex: 10,
    background: "rgba(0,0,0,0.6)",
    color: "#fff",
    border: "none",
    borderRadius: "16px",
    width: `${iconBtnSize}px`,
    height: `${iconBtnSize}px`,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 0,
  }), [iconBtnSize, deletePos.top, deletePos.right])
  
  const downloadButtonStyle = useMemo(() => ({
    position: "absolute",
    top: downloadPos.top,
    right: downloadPos.right,
    zIndex: 10,
    background: "rgba(0,0,0,0.6)",
    color: "#fff",
    border: "none",
    borderRadius: "16px",
    width: `${iconBtnSize}px`,
    height: `${iconBtnSize}px`,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 0,
    transition: "background 0.15s ease, transform 0.1s ease",
  }), [iconBtnSize, downloadPos.top, downloadPos.right])

  // Aktiver Zustand des Download-Buttons während der Browser den Blob holt –
  // gibt sofortiges visuelles Feedback (pinker Brand-Hintergrund + Spinner),
  // damit der User weiß, dass der Tap registriert wurde.
  const downloadButtonActiveStyle = useMemo(() => ({
    position: "absolute",
    top: downloadPos.top,
    right: downloadPos.right,
    zIndex: 10,
    background: "linear-gradient(135deg, #ff4fb8 0%, #ff85cc 100%)",
    color: "#fff",
    border: "none",
    borderRadius: "16px",
    width: `${iconBtnSize}px`,
    height: `${iconBtnSize}px`,
    cursor: "wait",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 0,
    boxShadow: "0 4px 12px rgba(255, 79, 184, 0.45)",
    transform: "scale(1.05)",
    transition: "background 0.15s ease, transform 0.1s ease",
  }), [iconBtnSize, downloadPos.top, downloadPos.right])

  const compactControls = effectiveColumns >= 3
  const usePromptButtonStyle = useMemo(() => ({
    position: "absolute",
    top: `${iconBtnGap}px`,
    left: `${iconBtnGap}px`,
    zIndex: 10,
    background: "rgba(217, 106, 147, 0.9)",
    color: "#fff",
    border: "none",
    borderRadius: compactControls ? "50%" : "14px",
    padding: compactControls ? "0" : isMobile ? "4px 8px" : "6px 12px",
    width: compactControls ? "28px" : "auto",
    height: compactControls ? "28px" : "auto",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
    fontSize: isMobile ? "10px" : "11px",
    fontWeight: "500",
    lineHeight: 0,
    maxWidth: isMobile ? "calc(100% - 40px)" : "none",
  }), [compactControls, isMobile, iconBtnGap])

  const zoomButtonStyle = useMemo(() => ({
    position: "absolute",
    top: zoomPos.top,
    right: zoomPos.right,
    zIndex: 10,
    background: "rgba(0,0,0,0.6)",
    color: "#fff",
    border: "none",
    borderRadius: "16px",
    width: `${iconBtnSize}px`,
    height: `${iconBtnSize}px`,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 0,
  }), [iconBtnSize, zoomPos.top, zoomPos.right])

  const useForVideoOverlayStyle = useMemo(() => ({
    position: "absolute",
    left: "50%",
    bottom: "14px",
    transform: "translateX(-50%)",
    zIndex: 11,
    background: "linear-gradient(135deg, #ff4fb8 0%, #ff85cc 50%, #ffb15a 100%)",
    color: "#fff",
    border: "none",
    borderRadius: "999px",
    padding: "11px 18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    fontSize: "13px",
    fontWeight: 700,
    letterSpacing: "0.2px",
    boxShadow: "0 8px 22px rgba(255, 79, 184, 0.55)",
    lineHeight: 1,
    cursor: "pointer",
    whiteSpace: "nowrap",
    fontFamily: "inherit",
    transition: "transform 0.15s ease, box-shadow 0.15s ease",
  }), [])

  const useForVideoSelectedOverlayStyle = useMemo(() => ({
    ...useForVideoOverlayStyle,
    background: "rgba(40, 40, 40, 0.92)",
    boxShadow: "0 8px 22px rgba(0,0,0,0.35)",
  }), [useForVideoOverlayStyle])
  
  const galleryImageStyle = useMemo(() => ({
    width: "100%",
    height: "auto",
    display: "block",
    borderRadius: "10px",
    cursor: "zoom-in",
  }), [])
  
  const placeholderStyle = useMemo(() => ({
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "#fce4ec",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }), [])
  
  const spinnerStyle = useMemo(() => ({
    border: "4px solid rgba(74, 26, 44, 0.1)",
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    borderLeftColor: "#d96a93",
    animation: "spin 1s ease infinite",
  }), [])
  const failedStyle = useMemo(() => ({
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "#FEE2E2",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#DC2626",
    textAlign: "center",
  }), [])
  
  const failedInnerStyle = useMemo(() => ({
    width: "100%",
    maxWidth: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    margin: "auto",
    padding: "8px 0",
    boxSizing: "border-box",
    background: "none",
  }), [])
  
  const errorMessageStyle = useMemo(() => ({
    fontSize: "16px",
    marginTop: "16px",
    opacity: 0.9,
    width: "100%",
    maxWidth: "100%",
    wordBreak: "break-word",
    whiteSpace: "pre-line",
    padding: "0 16px",
    boxSizing: "border-box",
    textAlign: "center",
    lineHeight: 1.5,
  }), [])

  const handleDownload = useCallback(async (mediaUrl, mediaId) => {
    // Sofort den aktiven State setzen (synchron im selben Tick wie der Klick),
    // damit der Button SOFORT visuelles Feedback gibt und der User nicht das
    // Gefühl hat, der Klick sei verloren gegangen.
    setDownloadingId(mediaId ?? "__pending__")
    try {
      const response = await fetch(mediaUrl)
      const blob = await response.blob()

      // Try to derive the extension from the blob MIME type, fall back to jpeg.
      let extension = 'jpeg'
      if (blob.type) {
        const mimeType = blob.type.split('/')[1]
        if (['jpeg', 'png', 'webp', 'gif', 'jpg', 'mp4', 'webm', 'quicktime'].includes(mimeType)) {
          extension = mimeType === 'quicktime' ? 'mov' : mimeType
        }
      }
      // Last-resort: peek at the URL extension.
      if (extension === 'jpeg' && /\.(mp4|mov|webm)(\?|$)/i.test(mediaUrl)) {
        extension = mediaUrl.match(/\.(mp4|mov|webm)/i)[1].toLowerCase()
      }

      const isVideo = ['mp4', 'webm', 'mov'].includes(extension)
      const mimeType = blob.type || (isVideo ? `video/${extension === 'mov' ? 'quicktime' : extension}` : `image/${extension}`)
      const fileName = `pinkcatai-${mediaId || Date.now()}.${extension}`

      // Auf Mobilgeräten via Web Share API teilen, damit der User "Bild sichern"
      // bzw. "In Galerie speichern" wählen kann → landet in der Fotos-Galerie
      // statt im Download-Ordner. Auf Desktop / nicht unterstützten Browsern
      // greift der klassische Download-Fallback.
      const file = new File([blob], fileName, { type: mimeType })
      const canShareFile =
        typeof navigator !== 'undefined' &&
        typeof navigator.canShare === 'function' &&
        typeof navigator.share === 'function' &&
        navigator.canShare({ files: [file] })

      const isMobileUA =
        typeof navigator !== 'undefined' &&
        /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '')

      if (canShareFile && isMobileUA) {
        try {
          await navigator.share({
            files: [file],
            title: fileName,
          })
          return
        } catch (shareErr) {
          // Wenn der User das Share-Sheet abbricht, NICHT auf Download
          // zurückfallen – sonst kommt unerwünscht doch eine Datei in
          // Downloads. Echte Fehler durchreichen → klassischer Fallback.
          if (shareErr && (shareErr.name === 'AbortError' || shareErr.name === 'NotAllowedError')) {
            return
          }
          // sonst Fallback auf Download
        }
      }

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Download failed:', error)
      alert('Download failed. Please try again.')
    } finally {
      setDownloadingId(null)
    }
  }, [])

  // GalleryItem is hoisted to module scope (see below). We just bundle the
  // styles + callbacks it needs into a stable `ctx` object here.
  const galleryItemCtx = useMemo(
    () => ({
      isMobile,
      compactControls,
      selectedVideoSourceUrl,
      onUsePrompt,
      onUseForVideo,
      onHideImage,
      setFullscreenImage,
      handleDownload,
      downloadingId,
      styles: {
        galleryImageContainerStyle,
        usePromptButtonStyle,
        downloadButtonStyle,
        downloadButtonActiveStyle,
        zoomButtonStyle,
        useForVideoOverlayStyle,
        useForVideoSelectedOverlayStyle,
        deleteButtonStyle,
        placeholderStyle,
        spinnerStyle,
        failedStyle,
        failedInnerStyle,
        errorMessageStyle,
        galleryImageStyle,
      },
    }),
    [
      isMobile,
      compactControls,
      selectedVideoSourceUrl,
      onUsePrompt,
      onUseForVideo,
      onHideImage,
      setFullscreenImage,
      handleDownload,
      downloadingId,
      galleryImageContainerStyle,
      usePromptButtonStyle,
      downloadButtonStyle,
      downloadButtonActiveStyle,
      zoomButtonStyle,
      useForVideoOverlayStyle,
      useForVideoSelectedOverlayStyle,
      deleteButtonStyle,
      placeholderStyle,
      spinnerStyle,
      failedStyle,
      failedInnerStyle,
      errorMessageStyle,
      galleryImageStyle,
    ]
  )

  return (
    <>
      <style>{spinKeyframes}</style>
      <div style={galleryAreaStyle}>
        {isLoadingInitial && (
          <div
            style={{
              textAlign: "center",
              color: "#8b4d6a",
              width: "100%",
              alignSelf: "center",
            }}
          >
            Loading gallery...
          </div>
        )}
        {!isLoadingInitial && galleryImages.length === 0 && (
          <div
            style={{
              textAlign: "center",
              color: "#8b4d6a",
              width: "100%",
              alignSelf: "center",
            }}
          >
            {emptyText}
          </div>
        )}
        {!isLoadingInitial && galleryImages.length > 0 && (
          <>
            <div style={galleryGridStyle}>
              {displayedImages.map((img) => (
                <GalleryItem key={img.id} img={img} ctx={galleryItemCtx} />
              ))}
            </div>
            
            {/* Load More Trigger - wird beobachtet um weitere Bilder zu laden */}
            {displayedCount < galleryImages.length && (
              <div
                ref={loadMoreTriggerRef}
                style={{
                  width: "100%",
                  padding: "20px",
                  textAlign: "center",
                  color: "#8b4d6a",
                  fontSize: "14px",
                }}
              >
                <div style={spinnerStyle}></div>
                <p style={{ marginTop: "12px" }}>Loading more images...</p>
              </div>
            )}
            
            {/* Info wenn alle Bilder geladen sind */}
            {displayedCount >= galleryImages.length && galleryImages.length > 4 && (
              <div
                style={{
                  width: "100%",
                  padding: "20px",
                  textAlign: "center",
                  color: "#ed9bb8",
                  fontSize: "13px",
                }}
              >
                All {galleryImages.length} images loaded
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}

export default React.memo(ImageGallery)